import { useState, useRef, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { 
  LayoutDashboard, 
  Map, 
  CalendarDays, 
  GraduationCap, 
  ClipboardCheck,
  Settings,
  Stethoscope,
  LogOut,
  Send,
  Shield,
  ChevronUp,
  Menu,
  X
} from "lucide-react";

export default function Sidebar({ isOpen, onClose }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    if (onClose) {
      onClose();
    }
  }, [location.pathname]);

  // Main navigation items (without Settings and Admin)
  const navItems = [
    { path: "/", label: "Dashboard", icon: LayoutDashboard },
    { path: "/map", label: "Clinical Sites", icon: Map },
    { path: "/rotations", label: "Rotations", icon: CalendarDays },
    { path: "/students", label: "Students", icon: GraduationCap },
    { path: "/requests", label: "Requests", icon: Send },
    { path: "/approvals", label: "Approvals", icon: ClipboardCheck },
  ];

  // User menu items
  const userMenuItems = [
    { path: "/settings", label: "Settings", icon: Settings },
  ];

  // Add admin link only for admin users
  if (user?.role === "admin") {
    userMenuItems.unshift({ path: "/admin", label: "Admin", icon: Shield });
  }

  const handleMenuItemClick = (path) => {
    setShowUserMenu(false);
    navigate(path);
    if (onClose) onClose();
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    logout();
  };

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`
          fixed left-0 top-0 h-screen w-64 bg-[#151A23] border-r border-slate-700 flex flex-col z-50
          transform transition-transform duration-300 ease-in-out
          md:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-4 sm:p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-cyan-400/10 rounded-xl flex items-center justify-center">
                <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />
              </div>
              <div>
                <h1 className="text-base sm:text-lg font-bold text-slate-100">Precepta</h1>
                <p className="text-xs text-slate-500 hidden sm:block">Smart Match</p>
              </div>
            </div>
            {/* Close button for mobile */}
            <button 
              onClick={onClose}
              className="lg:hidden p-2 text-slate-400 hover:text-slate-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 sm:p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? "bg-cyan-400/10 text-cyan-400"
                    : "text-slate-400 hover:text-slate-200 hover:bg-white/5"
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium text-sm sm:text-base">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* User Profile with Popup Menu */}
        <div className="p-3 sm:p-4 border-t border-slate-700 relative" ref={menuRef}>
          {/* Popup Menu */}
          {showUserMenu && (
            <div 
              className="absolute bottom-full left-3 right-3 sm:left-4 sm:right-4 mb-2 bg-[#1a2130] border border-slate-600 rounded-xl shadow-xl overflow-hidden"
              data-testid="user-menu-popup"
            >
              {userMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleMenuItemClick(item.path)}
                    className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                      isActive
                        ? "bg-cyan-400/10 text-cyan-400"
                        : "text-slate-300 hover:bg-slate-700/50 hover:text-white"
                    }`}
                    data-testid={`menu-${item.label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}
              <div className="border-t border-slate-700" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-400/10 transition-colors"
                data-testid="menu-logout"
              >
                <LogOut className="w-4 h-4" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          )}

          {/* User Profile Button */}
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg transition-colors ${
              showUserMenu ? "bg-slate-700/50" : "hover:bg-slate-700/30"
            }`}
            data-testid="user-profile-button"
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-cyan-500 to-violet-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-semibold text-sm sm:text-base">
                {user?.first_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-xs sm:text-sm font-medium text-slate-200 truncate">
                {user?.first_name && user?.last_name 
                  ? `${user.first_name} ${user.last_name}` 
                  : user?.first_name || user?.last_name || user?.email}
              </p>
              <p className="text-xs text-slate-500 truncate">{user?.organization || user?.role?.replace("_", " ")}</p>
            </div>
            <ChevronUp 
              className={`w-4 h-4 text-slate-400 transition-transform duration-200 flex-shrink-0 ${
                showUserMenu ? "rotate-0" : "rotate-180"
              }`} 
            />
          </button>
        </div>
      </aside>
    </>
  );
}

// Mobile Header Component
export function MobileHeader({ onMenuClick }) {
  return (
    <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-[#151A23] border-b border-slate-700 flex items-center px-4 z-30">
      <button 
        onClick={onMenuClick}
        className="p-2 text-slate-400 hover:text-slate-200 -ml-2"
        data-testid="mobile-menu-button"
      >
        <Menu className="w-6 h-6" />
      </button>
      <div className="flex items-center gap-2 ml-2">
        <Stethoscope className="w-5 h-5 text-cyan-400" />
        <span className="font-bold text-slate-100">Precepta</span>
      </div>
    </header>
  );
}
